import PropTypes from 'prop-types';

/**
 * PortfolioPermission Component
 *
 * A wrapper component that conditionally renders its children based on the user's
 * permissions in a portfolio. Supports both admin/viewer role checking and custom permission logic.
 *
 * @param {Object} portfolio - The portfolio object containing role and ownership information
 * @param {string} requiredPermission - The required permission level ('admin', 'viewer', or 'owner')
 * @param {function} customCheck - Custom permission check function that receives the portfolio object
 * @param {React.ReactNode} children - The content to render if permission is granted
 * @param {React.ReactNode} fallback - Optional content to render if permission is denied
 * @param {boolean} invert - If true, renders children when permission check fails (useful for "viewers only" content)
 */
const PortfolioPermission = ({
	portfolio,
	requiredPermission = 'admin',
	customCheck,
	children,
	fallback = null,
	invert = false,
}) => {
	// If no portfolio data is available, deny access by default
	if (!portfolio) {
		return invert ? children : fallback;
	}

	let hasPermission = false;

	// Use custom check function if provided
	if (customCheck && typeof customCheck === 'function') {
		hasPermission = customCheck(portfolio);
	} else {
		// Built-in permission checks
		switch (requiredPermission) {
			case 'admin':
				hasPermission = portfolio.isOwned || portfolio.role === 'admin';
				break;
			case 'owner':
				hasPermission = portfolio.isOwned;
				break;
			case 'viewer':
				hasPermission = true; // Everyone with portfolio access can view
				break;
			default:
				console.warn(
					`PortfolioPermission: Unknown permission level '${requiredPermission}'`
				);
				hasPermission = false;
		}
	}

	// Invert the permission check if requested
	const shouldRender = invert ? !hasPermission : hasPermission;

	return shouldRender ? children : fallback;
};

PortfolioPermission.propTypes = {
	portfolio: PropTypes.shape({
		isOwned: PropTypes.bool,
		role: PropTypes.oneOf(['admin', 'viewer']),
	}),
	requiredPermission: PropTypes.oneOf(['admin', 'viewer', 'owner']),
	customCheck: PropTypes.func,
	children: PropTypes.node.isRequired,
	fallback: PropTypes.node,
	invert: PropTypes.bool,
};

export default PortfolioPermission;
